/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {addVersionToMetricsConfig, isShell} from './util';
import Emitter from '@exc/emitter';
import {engine} from './runtimeDeferred';
import {Events} from '@exc/shared';
import internal from './internal';
import Metrics from './metrics/lazyMetrics';

interface Callback {(value?: any): void}

interface CallbackObject {
  [id: string]: Callback;
}

function throttleQueue(fn: Callback, timeout: number): Callback {
  let ready = true;
  return function (this: any, ...args): void {
    if (ready) {
      ready = false;
      setTimeout(() => {
        fn.apply(this, args);
        ready = true;
      }, timeout);
    }
  };
}

export default class Messenger extends Emitter {
  public inQueue: MessageEvent[] = [];
  public isConnected = false;
  public origin = '*';
  public queue: [string, Record<string, any>][] = [];
  public callbacks: {[type: string]: Callback | CallbackObject} = {};
  public appId: string | undefined = undefined;
  public metrics = new Metrics('exc.module-runtime.messenger');

  public constructor() {
    super();
    window.addEventListener('message', this.receive, false);
    // Sends ping event with * as origin because it doesn't contain
    // anything secure and we need the response of the first received post
    // message to determine what that valid origin is.
    this._sendMessage(Events.PING);
    // Wait 16ms/1 frame every time a post message is sent before clearing queue
    this.flushQueue = throttleQueue(this.flushQueue, 16);
  }

  private _sendMessage(type: string, value?: any): void {
    window.parent.postMessage({
      appId: this.appId,
      type,
      unifiedShell: 1,
      value
    }, this.origin);
  }

  public flushQueue(): void {
    // Ensure this.origin is set on the off-chance that we flush queue
    // before receiving the first post message.
    if (this.origin === '*') {
      return;
    }

    this.queue.length > 0 && this._sendMessage(Events.QUEUE, this.queue);
    this.queue = [];
  }

  public send(type: string, value?: any): void {
    // Consolidate Shell Settings Config Event if possible
    if (type === Events.SHELL_SETTINGS) {
      let currentValue = this.queue.find(([key]) => key === Events.SHELL_SETTINGS);
      if (currentValue) {
        Object.assign(currentValue[1], value);
        return;
      }
    } else if (type === Events.NOT_INITIALIZED) {
      // Bypass queue.
      this._sendMessage(type);
      return;
    }
    this.queue.push([type, value]);
    this.flushQueue();
  }

  public receive = (event: MessageEvent): void => {
    // Ensure the messages always come from adobe.com/io origins.
    if (!/(\/\/|\.)adobe.(com|io)(:[0-9]{4})?$/.test(event.origin)) {
      return;
    }

    const data = event.data || {};
    const type: string = data.type;

    // Only accept messages from unified shell
    if (data.unifiedShell !== 1) {
      return;
    }

    if (!this.isConnected) {
      if (type === Events.PONG) {
        this.isConnected = true;
        this.origin = event.origin;
        this.appId = data.appId;
        if (data.value) {
          const {metricsConfig, preloads} = data.value;
          const config = addVersionToMetricsConfig(metricsConfig);
          // Many SPAs load Runtime.js early but instantiate the Runtime object later.
          // For these scenarios, we send some useful information to the iframe via the PONG
          // event so that the iframe can do a few things ahead of Runtime instantiation such as
          // 1. Use EIM
          internal.configureMetrics(config);
          // 2. Preload resources (Currently only Brand SVGs)
          engine.preload(preloads);
          // PONG event is only dependant on the iframe loading Runtime.js
        }
        this.metrics.event('Messenger.connected', {appId: data.appId});
        this.flushQueue();
        this.inQueue.forEach(event => this.receive(event));
        this.inQueue = [];
      } else {
        this.metrics.info(`Event ${type || 'unknown'} received before connection - Queueing.`);
        this.inQueue.push(event);
        return;
      }
    }

    const value: any = data.value;
    const cb = this.callbacks[type];
    if (cb) {
      if (typeof(cb) === 'function') {
        cb(value);
      } else {
        cb[value.id] && cb[value.id](value);
      }
    }

    this.emit('message', event);
  };

  public setCallback = (type: string, callback?: Callback | CallbackObject | null): void => {
    if (!callback) {
      delete this.callbacks[type];
      return;
    }
    this.callbacks[type] = callback;
  };
}

export let messenger: Messenger;

if (!isShell() || process.env.NODE_ENV === 'test') {
  messenger = new Messenger();
}
